import moment from "moment";
import React from "react";

import {Card, Table, TableBody, TableCell, TableHead, TableRow, Tooltip,} from "@material-ui/core";
import clsx from "clsx";

import _ from "lodash";
import {useSelector} from "react-redux";

import {
    DisconnectedIcon,
    DragListDownIcon,
    DragListUpIcon,
    HighPriorityIcon,
    RefreshIcon,
    SignalIcon,
    SignalMediumIcon,
    SignalNoneIcon,
} from "../components/icons/themes/windows10";
import {DashboardWarehouseTable, WarehouseStatus} from "./types";
import {DashboardState} from "../reducers/dashboard";
import {useDestinationListStyles} from "../components/DestinationsList";
import AnimateHeight from "react-animate-height";

const twelveHoursAgo = moment().subtract(12, "hours");
const twentyFourHoursAgo = moment().subtract(24, "hours");

enum TableStatus {
    Good = "good",
    Warning = "warning",
    Bad = "bad",
}

const TableStatusClass = {
    [TableStatus.Good]: "text-grey-700",
    [TableStatus.Warning]: "text-yellow-700",
    [TableStatus.Bad]: "text-red-500",
};

const WarehouseStatusClass = {
    [TableStatus.Good]: "text-green-700",
    [TableStatus.Warning]: "text-yellow-700",
    [TableStatus.Bad]: "text-red-500",
};

const OverallWarehouseStatusText = {
    [TableStatus.Good]: "All tables up-to-date",
    [TableStatus.Warning]: "Some tables may be out-of-date",
    [TableStatus.Bad]: "Some tables are not syncing",
};

const getTableLastSyncedTime = (table: DashboardWarehouseTable): moment.Moment | null => {
    if (table.synced_to_time == null) {
        return null
    }

    return moment(table.synced_to_time);
}

const tableStatus = (table: DashboardWarehouseTable): TableStatus => {
    const lastSyncMoment = getTableLastSyncedTime(table);
    return !lastSyncMoment || lastSyncMoment.isBefore(twelveHoursAgo)
        ? !lastSyncMoment || lastSyncMoment.isBefore(twentyFourHoursAgo)
            ? TableStatus.Bad
            : TableStatus.Warning
        : TableStatus.Good;
};

/**
 * The status of a singel warehouse table.
 *
 * The wording here is confusing because this is an html "table row" inside an html "table" but it's used to
 * show the status of a single db "table" inside a warehouse.
 */
const DashboardWarehouseTableRow: React.FC<{
    table: DashboardWarehouseTable;
    warehouseStatus: WarehouseStatus;
    index: number;
}> = ({table, warehouseStatus, index}) => {
    const lastSyncMoment = getTableLastSyncedTime(table);
    const status = tableStatus(table);
    const hasAlert = status !== TableStatus.Good;
    const lastSyncAlertClass = TableStatusClass[status];
    const isActive = table.event_is_active_for_destination;

    return (
        <TableRow
            key={`${warehouseStatus.destination.id}-${index}-${
                isActive ? "active" : "inactive"
            }`}
        >
            <TableCell>
                <div
                    className={clsx("font-mono text-2xs", {
                        "text-grey-800": isActive,
                        "text-grey-500": !isActive,
                    })}
                >
                    {isActive ? (
                        <>{table.name}</>
                    ) : (
                        <div>
                            <Tooltip title="Disconnected"><DisconnectedIcon/></Tooltip> {table.name}
                        </div>
                    )}
                </div>
            </TableCell>
            <TableCell align="center">
                {
                    lastSyncMoment ?
                        (
                            <Tooltip
                                title={`${lastSyncMoment.format("MMMM Do YYYY, h:mm:ss a")} ${
                                    lastSyncMoment.format("z") || "UTC"
                                }`}
                            >
                                <div
                                    className={clsx(" text-xs", {
                                        [lastSyncAlertClass]: isActive,
                                        "text-grey-500": !isActive,
                                    })}
                                >
                                    {hasAlert && isActive && <HighPriorityIcon className="mr-2"/>}
                                    {lastSyncMoment.fromNow()}
                                </div>
                            </Tooltip>
                        ) : isActive && (
                        <Tooltip
                            title="This table is awaiting the first sync since it's been turned on"
                        >
                            <div className="text-xs animate-pulse">Awaiting initial sync</div>
                        </Tooltip>
                    )
                }
            </TableCell>
            <TableCell align="center">
                <a
                    href={`/events/schema/${table.event_def_id}`}
                    className={clsx("cursor-pointer rounded px-2 py-2 text-sm", {
                        "text-purple-800 hover:bg-purple-300": isActive,
                        "text-grey-600 hover:bg-grey-300": !isActive,
                    })}
                >
                    <Tooltip title={table.event_def_name}>
            <span>
              {_.truncate(table.event_def_name, {
                  length: 28,
              })}
            </span>
                    </Tooltip>
                </a>
            </TableCell>
        </TableRow>
    );
};

/**
 * The header of a warehouse status card. Shows the type, name, and overall status of the whole warehouse.
 */
const DashboardWarehouseDestinationCardHeader: React.FC<{
    warehouseStatus: WarehouseStatus;
    activeTables: DashboardWarehouseTable[];
    inactiveTables: DashboardWarehouseTable[];
    onClick: () => void;
    isOpen: boolean;
    allowClosing: boolean;
}> = ({allowClosing, isOpen, onClick, warehouseStatus, activeTables, inactiveTables}) => {
    const loading = useSelector(
        (state: { dashboard: DashboardState }) => state.dashboard.loading
    );
    const classes = useDestinationListStyles();
    const dest = warehouseStatus.warehouse_destination;
    const DestinationIcon = dest.logoSquare;

    let greenTables = 0,
        yellowTables = 0,
        redTables = 0;

    // Get overview
    activeTables.forEach((table) => {
        const status = tableStatus(table);

        if (status === TableStatus.Bad) {
            redTables += 1;
        } else if (status === TableStatus.Warning) {
            yellowTables += 1;
        } else if (status === TableStatus.Good) {
            greenTables += 1;
        }
    });

    let warehouseStatusIndicator = TableStatus.Good;
    if (redTables > 0) {
        warehouseStatusIndicator = TableStatus.Bad
    } else if (yellowTables > 0) {
        warehouseStatusIndicator = TableStatus.Warning
    }

    const warehouseOverallText =
        OverallWarehouseStatusText[warehouseStatusIndicator];

    return (
        <div className="pb-2 pt-4 px-4 flex flex-col group" onClick={onClick}>
            <div className="flex justify-between items-center">
                <div className="flex flex-col">
                    <div className={clsx(classes.destinationInfo, "mb-2")}>
                        {DestinationIcon && (
                            <DestinationIcon height={34} width={34}/>
                        )}
                        <div className={classes.destinationName}>{dest.name}</div>
                    </div>

                    <div className="text-grey-500 text-xs">
                        {loading && <RefreshIcon className="animate-spin mr-2"/>}
                        {activeTables.length} tables connected
                    </div>
                </div>

                <div
                    className={clsx(
                        "mt-2 flex items-center",
                        WarehouseStatusClass[warehouseStatusIndicator]
                    )}
                >
                <span className="text-lg">
                  {warehouseStatusIndicator === TableStatus.Good ? (
                      <SignalIcon className="mr-1"/>
                  ) : warehouseStatusIndicator === TableStatus.Warning ? (
                      <SignalMediumIcon className="mr-1"/>
                  ) : (
                      <SignalNoneIcon className="mr-1"/>
                  )}
                </span>
                    <span className="text-sm relative" style={{top: 3}}>
                      {warehouseOverallText}
                    </span>
                </div>
            </div>

            {allowClosing && <div className='flex justify-center group-hover:animate-bounce' onClick={onClick}>
                {isOpen ? <DragListDownIcon className={clsx("group-hover:text-black", {
                    'text-grey-400': !isOpen,
                    'text-grey-100': isOpen,
                })}/> : <DragListUpIcon className="text-grey-400 group-hover:text-black"/>}
            </div>}
        </div>
    )
}

/**
 * A single card that shows the entire status of one warehouse destination
 */
export const DashboardWarehouseDestinationCard: React.FC<{ warehouseStatus: WarehouseStatus; allowClosing: boolean; }> = ({
                                                                                                                              warehouseStatus,
                                                                                                                              allowClosing
                                                                                                                          }) => {
    const activeTables = warehouseStatus.tables.filter(
        (table) => table.event_is_active_for_destination
    );
    const inactiveTables = warehouseStatus.tables.filter(
        (table) => !table.event_is_active_for_destination
    );

    const [isOpen, setIsOpen] = React.useState(!allowClosing);

    const toggleClosed = React.useCallback(() => {
        if (allowClosing) {
            setIsOpen(!isOpen);
        }
    }, [setIsOpen, allowClosing, isOpen]);

    return (
        <Card
            className={clsx("w-full mb-4", {
                'cursor-pointer': allowClosing,
            })}
            key={warehouseStatus.destination.id}
        >
            <div>
                <DashboardWarehouseDestinationCardHeader
                    warehouseStatus={warehouseStatus}
                    activeTables={activeTables}
                    inactiveTables={inactiveTables}
                    onClick={toggleClosed}
                    allowClosing={allowClosing}
                    isOpen={isOpen}
                />

                <AnimateHeight duration={500} height={isOpen ? "auto" : 0}>
                    <Table className="w-full bg-white">
                        <TableHead>
                            <TableRow>
                                <TableCell>Table Name</TableCell>
                                <TableCell
                                    align="center"
                                    style={{maxWidth: "20%", minWidth: 167}}
                                >
                                    Last Sync
                                </TableCell>
                                <TableCell align="center" style={{width: "37%"}}>
                                    Event Name
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {activeTables.map((table, index) => (
                                <DashboardWarehouseTableRow
                                    key={table.name}
                                    table={table}
                                    index={index}
                                    warehouseStatus={warehouseStatus}
                                />
                            ))}
                            {inactiveTables.map((table, index) => (
                                <DashboardWarehouseTableRow
                                    key={table.name}
                                    table={table}
                                    index={index}
                                    warehouseStatus={warehouseStatus}
                                />
                            ))}
                        </TableBody>
                    </Table>
                </AnimateHeight>
            </div>
        </Card>
    );
}

export const DashboardPage: React.FC = () => {
    const dashboardData = useSelector((state: { dashboard: DashboardState }) => state.dashboard.data);
    const allowClosing = dashboardData.length > 1;

    React.useEffect(() => {
        // This makes the time generated with `moment.fromNow()` show as `28 hours ago` instead of `1 day ago`.
        moment.relativeTimeThreshold("m", 60);
        moment.relativeTimeThreshold("h", 24 * 26);
    }, []);

    if (dashboardData.length === 0) {
        return null;
    }

    return (
        <div className="flex-col flex w-full 2xl:w-8/12 flex-shrink-0 mr-2 ">
            {dashboardData.map((warehouseStatus) => <DashboardWarehouseDestinationCard
                allowClosing={allowClosing}
                warehouseStatus={warehouseStatus}/>)}
        </div>
    );
};
